import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import './style.css';

class JoyStick {
  constructor(options) {
    const circle = document.createElement('div');
    circle.style.cssText = "position:fixed; bottom:35px; width:80px; height:80px; background:rgba(126, 126, 126, 0.5); border:#444 solid medium; border-radius:50%; left:50%; transform:translateX(-50%);";
    const thumb = document.createElement('div');
    thumb.style.cssText = "position: absolute; left: 20px; top: 20px; width: 40px; height: 40px; border-radius: 50%; background: #fff;";
    circle.appendChild(thumb);
    document.body.appendChild(circle);
    this.domElement = thumb;
    this.maxRadius = options.maxRadius || 40;
    this.maxRadiusSquared = this.maxRadius * this.maxRadius;
    this.onMove = options.onMove;
    this.game = options.game;
    this.origin = { left: this.domElement.offsetLeft, top: this.domElement.offsetTop };
    this.rotationDamping = 0.06;
    this.moveDamping = 0.01;
    this.offset = { x: 0, y: 0 };
    
    if ('ontouchstart' in window) {
      this.domElement.addEventListener('touchstart', this.tap.bind(this));
      document.addEventListener('touchmove', this.move.bind(this), { passive: false });
      document.addEventListener('touchend', this.up.bind(this));
    }
    this.domElement.addEventListener('mousedown', this.tap.bind(this));
    document.addEventListener('mousemove', this.move.bind(this));
    document.addEventListener('mouseup', this.up.bind(this));
  }

  tap(evt) {
    this.offset = { x: 0, y: 0 };
    const touch = evt.type === 'mousedown' ? evt : evt.touches[0];
    const rect = this.domElement.getBoundingClientRect();
    this.origin.left = rect.left + this.maxRadius;
    this.origin.top = rect.top + this.maxRadius;
    this.offset = { x: touch.clientX - this.origin.left, y: touch.clientY - this.origin.top };
  }

  move(evt) {
    if (this.offset.x === 0 && this.offset.y === 0) return;
    
    evt.preventDefault();
    const touch = evt.type === 'mousemove' ? evt : evt.touches[0];
    const dx = touch.clientX - this.origin.left;
    const dy = touch.clientY - this.origin.top;
    const distance = dx * dx + dy * dy;
    
    if (distance < this.maxRadiusSquared) {
      this.domElement.style.left = dx + 20 + 'px';
      this.domElement.style.top = dy + 20 + 'px';
      
      // Add debug logging for joystick values
      console.log('Joystick Raw Values:', {
        dx: dx/this.maxRadius,
        dy: dy/this.maxRadius,
        distance: Math.sqrt(distance)
      });
      
      if (this.onMove) this.onMove(dx/this.maxRadius, dy/this.maxRadius);
    }
  }

  up(evt) {
    if (this.offset.x === 0 && this.offset.y === 0) return;
    if (this.onMove) this.onMove(0, 0);
    this.offset = { x: 0, y: 0 };
    this.domElement.style.left = 20 + 'px';
    this.domElement.style.top = 20 + 'px';
  }
}

class Game {
  constructor() {
    // Check mobile first
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const canvas = document.querySelector('#game');
    if (!canvas) {
      console.error('No #game canvas found');
      return;
    }

    // Scene setup
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 20000);
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      canvas: canvas
    });

    // Camera initial position - closer to duck for better perspective
    this.camera.position.set(0, 100, 200);
    this.camera.lookAt(0, 0, 0);
    
    // Adjust orbit controls for third-person view
    this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.orbitControls.enableDamping = true;
    this.orbitControls.dampingFactor = 0.05;
    this.orbitControls.screenSpacePanning = false;
    this.orbitControls.minDistance = 100;
    this.orbitControls.maxDistance = 300;
    this.orbitControls.maxPolarAngle = Math.PI / 2;
    this.orbitControls.enabled = false;  // Disable orbit controls since we're using third-person
    
    // Player physics
    this.gravity = 0.05;  // Reduced from 0.15 for gentler fall
    this.jumpForce = 2;
    this.isJumping = false;
    this.verticalVelocity = 0;
    this.groundLevel = 20;
    this.maxHeight = 2000;
    this.isFlying = false;
    this.flyingForce = 0.3;
    this.maxFlySpeed = 5;
    this.airResistance = 0.02;  // Added air resistance factor
    
    // Double tap detection
    this.lastTapTime = 0;
    this.doubleTapDelay = 300;
    
    // Player
    this.player = this.createPlayer();
    this.playerSpeed = 3;  // Increased for larger map
    this.playerRotationSpeed = 0.1;
    this.moveVector = new THREE.Vector3();
    
    // Add keyboard controls state
    this.keys = {
      ArrowUp: false,
      ArrowDown: false,
      ArrowLeft: false,
      ArrowRight: false
    };
    
    // Add collision detection properties
    this.raycaster = new THREE.Raycaster();
    this.collisionObjects = [];
    this.currentGroundHeight = this.groundLevel;
    
    this.init();
    this.createWorld();
    this.setupControls();
    this.animate();
  }

  init() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;

    // Handle window resize
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  setupControls() {
    const startButton = document.getElementById('startButton');
    if (!startButton) {
      console.error('No start button found');
      return;
    }

    if (this.isMobile) {
      // Mobile setup
      startButton.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.startGame();
      });

      // Add double tap detection for jumping
      document.addEventListener('touchstart', (e) => {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - this.lastTapTime;
        
        if (tapLength < this.doubleTapDelay && tapLength > 0) {
          // Double tap detected
          e.preventDefault();
          this.jump();
        }
        this.lastTapTime = currentTime;
      });
    } else {
      // Desktop setup
      startButton.addEventListener('click', () => {
        this.startGame();
      });

      // Add keyboard controls
      document.addEventListener('keydown', (e) => {
        if (e.code === 'Space') {
          this.jump();
        }
        if (e.code in this.keys) {
          this.keys[e.code] = true;
        }
      });

      document.addEventListener('keyup', (e) => {
        if (e.code === 'Space') {
          this.stopFlying();
        }
        if (e.code in this.keys) {
          this.keys[e.code] = false;
        }
      });
    }
  }

  startGame() {
    const startButton = document.getElementById('startButton');
    const jumpIndicator = document.querySelector('.jump-indicator');
    const jumpText = document.querySelector('.jump-text');
    
    if (startButton) {
      startButton.style.display = 'none';
    }
    
    // Hide jump indicators
    if (jumpIndicator) {
      jumpIndicator.style.display = 'none';
    }
    if (jumpText) {
      jumpText.style.display = 'none';
    }

    if (this.isMobile) {
      this.setupMobileControls();
    }
  }

  createPlayer() {
    const duckGroup = new THREE.Group();

    // Duck body - oval shaped
    const bodyGeometry = new THREE.SphereGeometry(15, 32, 32);
    bodyGeometry.scale(1.5, 1, 1);  // Make it more oval
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0xFFFFFF });  // White base
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    duckGroup.add(body);

    // Duck head
    const headGeometry = new THREE.SphereGeometry(10, 32, 32);
    const headMaterial = new THREE.MeshStandardMaterial({ color: 0x2D5A27 });  // Green head
    const head = new THREE.Mesh(headGeometry, headMaterial);
    head.position.set(15, 10, 0);  // Position relative to body
    duckGroup.add(head);

    // Duck bill
    const billGeometry = new THREE.ConeGeometry(4, 12, 4);
    billGeometry.rotateZ(-Math.PI / 2);  // Rotate to point forward
    const billMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA500 });  // Orange bill
    const bill = new THREE.Mesh(billGeometry, billMaterial);
    bill.position.set(22, 10, 0);  // Position at front of head
    duckGroup.add(bill);

    // Duck wings (brown patches on sides)
    const wingGeometry = new THREE.SphereGeometry(12, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2);
    const wingMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });  // Brown wings
    
    // Left wing
    const leftWing = new THREE.Mesh(wingGeometry, wingMaterial);
    leftWing.rotation.z = Math.PI / 2;
    leftWing.position.set(0, 0, -12);
    duckGroup.add(leftWing);
    
    // Right wing
    const rightWing = new THREE.Mesh(wingGeometry, wingMaterial);
    rightWing.rotation.z = -Math.PI / 2;
    rightWing.position.set(0, 0, 12);
    duckGroup.add(rightWing);

    // Duck feet
    const footGeometry = new THREE.BoxGeometry(12, 2, 8);
    const footMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA500 });  // Orange feet
    
    // Left foot
    const leftFoot = new THREE.Mesh(footGeometry, footMaterial);
    leftFoot.position.set(-10, -10, -8);
    duckGroup.add(leftFoot);
    
    // Right foot
    const rightFoot = new THREE.Mesh(footGeometry, footMaterial);
    rightFoot.position.set(-10, -10, 8);
    duckGroup.add(rightFoot);

    // Add black dot for eye
    const eyeGeometry = new THREE.SphereGeometry(2, 16, 16);
    const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });
    
    // Right eye
    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(18, 13, 6);
    duckGroup.add(rightEye);
    
    // Left eye
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(18, 13, -6);
    duckGroup.add(leftEye);

    // Set the entire duck's initial position
    duckGroup.position.y = this.groundLevel;
    duckGroup.rotation.y = Math.PI;  // Face forward (towards negative z) initially
    duckGroup.castShadow = true;
    duckGroup.receiveShadow = true;
    
    this.scene.add(duckGroup);
    return duckGroup;
  }

  setupMobileControls() {
    this.joystick = new JoyStick({
      game: this,
      maxRadius: 40,
      onMove: (forward, turn) => {
        this.moveVector.x = turn * this.playerSpeed;
        this.moveVector.z = forward * this.playerSpeed;
      }
    });
  }

  createKingsCollegeChapel(x, z) {
    const chapelGroup = new THREE.Group();
    
    // Main chapel body - proportions matching the photo
    const mainBody = new THREE.BoxGeometry(300, 300, 800);
    const stoneMaterial = new THREE.MeshStandardMaterial({
      color: 0xE8D7B6,  // Warmer limestone color matching the photo
      roughness: 0.7,
      metalness: 0.1
    });
    const chapel = new THREE.Mesh(mainBody, stoneMaterial);
    chapel.position.y = 150;
    chapelGroup.add(chapel);

    // Create the front facade with Gothic details
    this.createChapelFacade(chapelGroup, stoneMaterial);

    // Side buttresses and pinnacles
    this.createSideButtresses(chapelGroup, stoneMaterial);

    // Roof with decorative ridge
    const roofGeometry = new THREE.BoxGeometry(320, 40, 820);
    const roofMaterial = new THREE.MeshStandardMaterial({
      color: 0x998877,  // Lighter grey for limestone roof
      roughness: 0.8
    });
    const roof = new THREE.Mesh(roofGeometry, roofMaterial);
    roof.position.y = 320;
    chapelGroup.add(roof);

    // Position and rotate to match map orientation
    chapelGroup.position.set(x, 0, z);
    chapelGroup.rotation.y = Math.PI / 6;
    chapelGroup.castShadow = true;
    chapelGroup.receiveShadow = true;
    this.scene.add(chapelGroup);
  }

  createChapelFacade(chapelGroup, stoneMaterial) {
    // Large Gothic window frame
    const windowFrameGeometry = new THREE.BoxGeometry(200, 240, 20);
    const windowFrame = new THREE.Mesh(windowFrameGeometry, stoneMaterial);
    windowFrame.position.set(0, 170, -390);
    chapelGroup.add(windowFrame);

    // Gothic window glass
    const windowMaterial = new THREE.MeshStandardMaterial({
      color: 0x88aacc,
      metalness: 0.9,
      roughness: 0.1,
      opacity: 0.7,
      transparent: true
    });
    const windowGeometry = new THREE.BoxGeometry(180, 220, 10);
    const window = new THREE.Mesh(windowGeometry, windowMaterial);
    window.position.set(0, 170, -385);
    chapelGroup.add(window);

    // Front towers (more detailed)
    const towerGeometry = new THREE.BoxGeometry(50, 400, 50);
    [-140, 140].forEach(xPos => {
      const tower = new THREE.Mesh(towerGeometry, stoneMaterial);
      tower.position.set(xPos, 200, -390);
      chapelGroup.add(tower);

      // Add decorative pinnacles on towers
      for (let i = 0; i < 4; i++) {
        const pinnacleGeometry = new THREE.ConeGeometry(10, 40, 4);
        const pinnacle = new THREE.Mesh(pinnacleGeometry, stoneMaterial);
        pinnacle.position.set(
          xPos + (i % 2 === 0 ? 15 : -15),
          380,
          -390 + (i < 2 ? 15 : -15)
        );
        chapelGroup.add(pinnacle);
      }
    });
  }

  createSideButtresses(chapelGroup, stoneMaterial) {
    // Flying buttresses with decorative elements
    for (let z = -350; z <= 350; z += 100) {
      [-150, 150].forEach(xPos => {
        // Main buttress
        const buttressGeometry = new THREE.BoxGeometry(30, 280, 40);
        const buttress = new THREE.Mesh(buttressGeometry, stoneMaterial);
        buttress.position.set(xPos, 140, z);
        chapelGroup.add(buttress);

        // Flying support
        const supportGeometry = new THREE.BoxGeometry(60, 20, 40);
        const support = new THREE.Mesh(supportGeometry, stoneMaterial);
        support.position.set(xPos * 0.8, 200, z);
        support.rotation.z = xPos > 0 ? -Math.PI / 6 : Math.PI / 6;
        chapelGroup.add(support);

        // Decorative pinnacle on top
        const pinnacleGeometry = new THREE.ConeGeometry(8, 40, 4);
        const pinnacle = new THREE.Mesh(pinnacleGeometry, stoneMaterial);
        pinnacle.position.set(xPos, 290, z);
        chapelGroup.add(pinnacle);

        // Side windows between buttresses
        if (z < 350) {
          const windowGeometry = new THREE.BoxGeometry(5, 160, 60);
          const windowMaterial = new THREE.MeshStandardMaterial({
            color: 0x88aacc,
            metalness: 0.9,
            roughness: 0.1,
            opacity: 0.7,
            transparent: true
          });
          const window = new THREE.Mesh(windowGeometry, windowMaterial);
          window.position.set(xPos * 0.7, 180, z + 50);
          chapelGroup.add(window);
        }
      });
    }
  }

  createWorld() {
    // Ground - much larger rectangular plane
    const groundGeometry = new THREE.PlaneGeometry(100000, 200000);
    const groundMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x90a955,
      roughness: 0.8
    });
    this.ground = new THREE.Mesh(groundGeometry, groundMaterial);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.receiveShadow = true;
    this.scene.add(this.ground);

    // Adjust shadow camera to match new ground size
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(2000, 2000, 2000);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 4096;
    directionalLight.shadow.mapSize.height = 4096;
    directionalLight.shadow.camera.near = 5;
    directionalLight.shadow.camera.far = 20000;
    directionalLight.shadow.camera.left = -10000;
    directionalLight.shadow.camera.right = 10000;
    directionalLight.shadow.camera.top = 7500;
    directionalLight.shadow.camera.bottom = -7500;
    this.scene.add(directionalLight);

    // Rest of the world creation remains unchanged
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambientLight);

    // River Cam - keep current scale
    const riverShape = new THREE.Shape();
    riverShape.moveTo(-5000, -4000);
    riverShape.quadraticCurveTo(-3000, -3000, -2000, -1000);
    riverShape.quadraticCurveTo(-1500, 0, -1000, 1000);
    riverShape.lineTo(-750, 1000);
    riverShape.quadraticCurveTo(-1250, 0, -1750, -1000);
    riverShape.quadraticCurveTo(-2750, -3000, -4750, -4000);
    
    const riverGeometry = new THREE.ShapeGeometry(riverShape);
    const riverMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a90e2,
      metalness: 0.8,
      roughness: 0.2
    });
    
    const river = new THREE.Mesh(riverGeometry, riverMaterial);
    river.rotation.x = -Math.PI / 2;
    river.position.y = 1;
    this.scene.add(river);

    // Main college buildings - keep current scale
    this.createKingsCollegeChapel(-1000, 1000);
    this.addBuilding(600, 500, 600, 0xc17f59, 0, 0, 1250);      // Trinity College
    this.addBuilding(600, 550, 600, 0x9d8174, 1000, 0, 750);    // St John's College

    // Main street buildings - keep current scale
    for (let i = -1500; i < 1500; i += 500) {
      this.addBuilding(
        250 + Math.random() * 150,
        400 + Math.random() * 200,
        250 + Math.random() * 150,
        0xe6d4c0,
        i,
        0,
        0
      );
    }

    // Smaller buildings - keep current scale
    for (let i = 0; i < 30; i++) {
      const x = -2000 + Math.random() * 4000;
      const z = -2000 + Math.random() * 4000;
      if (z > ((x + 2000) * (x + 2000) / 5000) - 500) {  // Away from river
        this.addBuilding(
          150 + Math.random() * 100,
          200 + Math.random() * 150,
          150 + Math.random() * 100,
          0xa67d5d,
          x,
          0,
          z
        );
      }
    }
  }

  addBuilding(width, height, depth, color, x, y, z) {
    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({ 
      color: color,
      roughness: 0.7,
      metalness: 0.1
    });
    const building = new THREE.Mesh(geometry, material);
    building.position.set(x, y + height/2, z);
    building.castShadow = true;
    building.receiveShadow = true;
    this.scene.add(building);
    
    // Add building to collision objects
    this.collisionObjects.push(building);
  }

  jump() {
    // Start flying when space is pressed
    this.isFlying = true;
  }

  stopFlying() {
    // Stop flying when space is released
    this.isFlying = false;
  }

  checkCollisions() {
    // Cast a ray downward from the duck's position
    const rayStart = new THREE.Vector3(
      this.player.position.x,
      this.player.position.y + 10, // Start slightly above current position
      this.player.position.z
    );
    const rayDirection = new THREE.Vector3(0, -1, 0);
    this.raycaster.set(rayStart, rayDirection);

    // Check for intersections with all collision objects
    const intersects = this.raycaster.intersectObjects([...this.collisionObjects, this.ground]);
    
    if (intersects.length > 0) {
      const collision = intersects[0]; // Get closest collision
      const collisionHeight = collision.point.y;
      
      // Update current ground height based on what we're standing on
      this.currentGroundHeight = collisionHeight;
      
      // If we're below the collision point, move up to it
      if (this.player.position.y < collisionHeight) {
        this.player.position.y = collisionHeight;
        this.verticalVelocity = 0;
        this.isJumping = false;
      }
    } else {
      // If no collision detected, use default ground level
      this.currentGroundHeight = this.groundLevel;
    }
  }

  updatePlayerPhysics() {
    if (this.isFlying) {
      // Apply upward force while flying, but limit the speed
      this.verticalVelocity = Math.min(this.verticalVelocity + this.flyingForce, this.maxFlySpeed);
      
      // Limit maximum height
      if (this.player.position.y >= this.maxHeight) {
        this.player.position.y = this.maxHeight;
        this.verticalVelocity = 0;
      }
    } else {
      // Gradual descent with air resistance
      const fallSpeed = Math.abs(this.verticalVelocity);
      const resistance = fallSpeed * this.airResistance;
      const effectiveGravity = Math.max(this.gravity * (1 - resistance), 0.01);
      
      // Apply gravity with increasing air resistance at higher speeds
      this.verticalVelocity -= effectiveGravity;
      
      // Limit maximum fall speed
      this.verticalVelocity = Math.max(this.verticalVelocity, -2.0);
    }

    // Update vertical position
    this.player.position.y += this.verticalVelocity;

    // Check for collisions with objects
    this.checkCollisions();

    // Ground collision with gentle landing
    if (this.player.position.y <= this.currentGroundHeight) {
      this.player.position.y = this.currentGroundHeight;
      this.verticalVelocity = 0;
      this.isJumping = false;
    }
  }

  updatePlayer() {
    // Update physics
    this.updatePlayerPhysics();

    if (this.isMobile) {
      // Mobile movement using joystick in local coordinates
      if (this.moveVector.length() > 0) {
        // Add debug logging for movement vector
        console.log('Movement Vector:', {
          x: this.moveVector.x,
          z: this.moveVector.z,
          length: this.moveVector.length()
        });
        
        // Create movement direction in world space
        const moveDir = new THREE.Vector3(
          this.moveVector.x,  // Right is positive X
          0,
          this.moveVector.z   // Forward is negative Z
        );
        
        // Scale the movement
        moveDir.multiplyScalar(15);
        
        // Debug log for scaled movement
        console.log('Scaled Movement:', {
          x: moveDir.x,
          z: moveDir.z,
          length: moveDir.length()
        });
        
        // Update rotation if there's significant movement
        if (moveDir.length() > 0.1) {
          const angle = Math.atan2(moveDir.x, moveDir.z) + Math.PI;  // Add PI to face the movement direction
          this.player.rotation.y = angle;
          
          // Debug log for rotation
          console.log('Duck Rotation:', {
            angle: angle,
            degrees: (angle * 180 / Math.PI)
          });
        }
        
        // Apply movement directly in world space
        const beforePosition = this.player.position.clone();
        this.player.position.add(moveDir);
        
        // Debug log for position change
        console.log('Position Change:', {
          before: {
            x: beforePosition.x,
            z: beforePosition.z
          },
          after: {
            x: this.player.position.x,
            z: this.player.position.z
          },
          delta: {
            x: this.player.position.x - beforePosition.x,
            z: this.player.position.z - beforePosition.z
          }
        });
      }
    } else {
      // Desktop movement using arrow keys - relative to duck's facing direction
      const moveSpeed = 3;
      let forward = 0;

      // Get input
      if (this.keys.ArrowUp) forward += 1;
      if (this.keys.ArrowDown) forward -= 1;
      if (this.keys.ArrowRight) {
        this.player.rotation.y += 0.1;
      }
      if (this.keys.ArrowLeft) {
        this.player.rotation.y -= 0.1;
      }

      // Calculate movement based on duck's local coordinate system
      if (forward !== 0) {
        // Create forward direction vector in local space
        const forward_dir = new THREE.Vector3(1, 0, 0);
        // Rotate it to match duck's orientation
        forward_dir.applyQuaternion(this.player.quaternion);
        // Scale by movement speed and direction
        forward_dir.multiplyScalar(moveSpeed * forward);
        
        // Apply movement in local space
        this.player.position.add(forward_dir);
      }
    }

    // Update camera to follow duck from behind at 45 degrees
    const cameraDistance = 1000;  // Increased from 150
    const cameraHeight = 1550;    // Increased from cameraDistance
    const lookAtHeight = 45;
    
    // Calculate camera position based on duck's rotation
    const cameraAngle = this.player.rotation.y;
    
    // Calculate camera position - changed signs to position behind
    const cameraX = this.player.position.x - Math.sin(cameraAngle) * cameraDistance;
    const cameraY = this.player.position.y + cameraHeight;
    const cameraZ = this.player.position.z - Math.cos(cameraAngle) * cameraDistance;
    
    // Smoothly move camera to new position
    this.camera.position.lerp(new THREE.Vector3(cameraX, cameraY, cameraZ), 0.1);
    
    // Look at point slightly above the duck
    const lookAtPoint = new THREE.Vector3(
      this.player.position.x,
      this.player.position.y + lookAtHeight,
      this.player.position.z
    );
    this.camera.lookAt(lookAtPoint);
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    this.updatePlayer();
    this.renderer.render(this.scene, this.camera);
  }
}

// Start the game
console.log('Starting game...');
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing game...');
  new Game();
}); 